import { useMemo } from 'react';
import type { Marca, MarcaData } from '@/api/laminas';
import { parsearMarca } from '@/api/laminas';
import { esMedida } from './marcado';

// La capa de anotaciones, dibujada como UN solo SVG en el espacio de píxeles
// de la hoja. Eso es lo que la hace barata: el `viewBox` es `0 0 ancho alto`,
// así que las coordenadas crudas del GeoJSON se pintan tal cual y el zoom lo
// hace el `transform` del padre — nunca se recalculan puntos al hacer pan o
// zoom.
//
// `vectorEffect="non-scaling-stroke"` mantiene el grosor en píxeles de
// PANTALLA: sin eso, al alejarse las líneas desaparecen y al acercarse se
// vuelven brochazos.
//
// SUPUESTO NO VERIFICADO: se asume el eje Y hacia ABAJO (origen arriba-izquierda,
// como la imagen). Es coherente con el chinche medido (pos_y 364 → 6,69 m desde
// arriba), pero no se probó contra una marca en el borde inferior. Si alguna
// anotación aparece espejada verticalmente, esto es lo primero que hay que mirar.

interface Props {
  anchoPx: number;
  altoPx: number;
  marcas: Marca[];
  /** Factor de escala actual, solo para dimensionar el texto en pantalla. */
  escala: number;
  /** El puente píxeles↔metros de ESTA hoja. Null = hoja sin calibrar. */
  metrosPorPixel?: number | null;
  idSeleccionada?: number | null;
  /** Sin esto las marcas no reciben punteros. Ver el comentario de abajo. */
  onAgarrar?: (id: number, ev: React.PointerEvent) => void;
  /**
   * La marca que se está arrastrando ahora mismo, con su corrimiento. Se pinta
   * como un `transform` del grupo y no moviendo las coordenadas: el SVG ya sabe
   * trasladar, así que arrastrar una mano alzada de 200 puntos no recalcula
   * nada en cada `pointermove`. El dato real se corre una sola vez, al soltar.
   */
  desplazada?: { id: number; dx: number; dy: number } | null;
}

type Punto = [number, number];

// El valor de una medida NO se guarda: se recalcula desde la geometría cada vez
// que se pinta. Por eso recalibrar una hoja corrige todas sus medidas de una, y
// por eso una medida sobre una hoja sin calibrar muestra píxeles en vez de
// mentir metros.
function largoEnMetros(pts: Punto[], mpp: number): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return total * mpp;
}

/** Área por la fórmula del cordón de zapato, en m². */
function areaEnMetros(anillo: Punto[], mpp: number): number {
  let doble = 0;
  for (let i = 0; i < anillo.length; i++) {
    const [x1, y1] = anillo[i];
    const [x2, y2] = anillo[(i + 1) % anillo.length];
    doble += x1 * y2 - x2 * y1;
  }
  return Math.abs(doble / 2) * mpp * mpp;
}

/** La etiqueta de una marca: la medida recalculada si la hay, si no la nota. */
function etiqueta(d: MarcaData, mpp?: number | null): string | null {
  if (esMedida(d)) {
    // Sin calibrar NO se rotula: una hoja con seis medidas mostraba seis veces
    // "sin calibrar" apiladas sobre el plano (reportado el 16-oct-2026). El aviso
    // es uno solo y vive en la banda del visor, que además ofrece calibrar.
    if (!mpp) return null;
    const geo = d.geometry;
    if (geo?.type === 'Polygon') {
      const anillo = comoPuntos(((geo.coordinates as Punto[][]) || [])[0]);
      return anillo.length >= 3 ? `${areaEnMetros(anillo, mpp).toFixed(2)} m²` : null;
    }
    const pts = comoPuntos(geo?.coordinates);
    return pts.length >= 2 ? `${largoEnMetros(pts, mpp).toFixed(2)} m` : null;
  }
  return d.properties?.description || null;
}

/** La punta de una flecha, en el espacio de píxeles de la hoja. */
function Punta({ a, b, color, grosor }: { a: Punto; b: Punto; color: string; grosor: number }) {
  const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const l = grosor * 7;
  const abre = 0.42;
  const pts = [
    b,
    [b[0] - l * Math.cos(ang - abre), b[1] - l * Math.sin(ang - abre)],
    [b[0] - l * Math.cos(ang + abre), b[1] - l * Math.sin(ang + abre)],
  ];
  return <polygon points={pts.map(([x, y]) => `${x},${y}`).join(' ')} fill={color} stroke="none" />;
}

function comoPuntos(coords: unknown): Punto[] {
  return Array.isArray(coords)
    ? (coords as Punto[]).filter(p => Array.isArray(p) && p.length >= 2)
    : [];
}

function Figura({ d }: { d: MarcaData }) {
  const p = d.properties || {} as MarcaData['properties'];
  const color = p.color || '#C96C6C';
  const grosor = p.width || 2;
  const relleno = p.fill || 'none';
  const comun = {
    stroke: color,
    strokeWidth: grosor,
    fill: relleno,
    // El resaltador es un trazo normal con opacidad: así deja ver el plano
    // debajo sin necesitar un modo de mezcla ni una capa aparte.
    ...(p.opacidad !== undefined ? { strokeOpacity: p.opacidad } : {}),
    vectorEffect: 'non-scaling-stroke' as const,
    // El halo de selección se pinta como un segundo trazo por debajo (ver abajo).
    strokeLinejoin: 'round' as const,
    strokeLinecap: 'round' as const,
  };

  const geo = d.geometry;
  if (!geo) return null;

  switch (geo.type) {
    case 'Point': {
      const [x, y] = (geo.coordinates as Punto) || [0, 0];
      // Una nota de texto NO lleva figura: su dibujo ES la etiqueta, que la
      // pinta el bloque de abajo. Pintarle además un punto la ensucia.
      if (p.style === 'text') return null;
      // Un símbolo de catálogo (`symbolId`) todavía no se resuelve: hasta tener
      // el catálogo propio se marca el punto, no se inventa el glifo.
      return <circle cx={x} cy={y} r={grosor * 4} {...comun} fill={color} />;
    }
    case 'LineString': {
      const pts = comoPuntos(geo.coordinates);
      if (pts.length < 2) return null;
      const linea = <polyline points={pts.map(([x, y]) => `${x},${y}`).join(' ')} {...comun} fill="none" />;
      if (p.style !== 'arrow') return linea;
      // La punta se dibuja como geometría propia y no con un <marker>: un
      // marker se escala con `strokeWidth`, y acá el trazo es non-scaling, así
      // que la punta quedaría de un tamaño distinto al de la línea en cada zoom.
      return (
        <>
          {linea}
          <Punta a={pts[pts.length - 2]} b={pts[pts.length - 1]} color={color} grosor={grosor} />
          {/* La doble flecha es la misma punta mirando al otro lado: se le pasa
              el segmento invertido y no hace falta otra función. */}
          {p.doblePunta && <Punta a={pts[1]} b={pts[0]} color={color} grosor={grosor} />}
        </>
      );
    }
    case 'MultiLineString': {
      const tramos = (geo.coordinates as Punto[][]) || [];
      return (
        <>
          {tramos.map((t, i) => (
            <polyline key={i} points={comoPuntos(t).map(([x, y]) => `${x},${y}`).join(' ')}
              {...comun} fill="none" />
          ))}
        </>
      );
    }
    case 'Polygon': {
      const anillo = comoPuntos(((geo.coordinates as Punto[][]) || [])[0]);
      if (anillo.length < 3) return null;
      // Un `rectangle` llega como polígono de 4 puntos: no hace falta un caso
      // aparte, el mismo <polygon> lo cubre.
      return <polygon points={anillo.map(([x, y]) => `${x},${y}`).join(' ')}
        {...comun} fill={relleno === 'none' ? 'none' : relleno}
        fillOpacity={relleno === 'none' ? 0 : 0.25} />;
    }
    default:
      return null;
  }
}

export function CapaMarcas({ anchoPx, altoPx, marcas, escala, metrosPorPixel,
                            idSeleccionada, onAgarrar, desplazada }: Props) {
  // Parsear una vez por cambio de lista: el JSON de una marca no cambia entre
  // renders de pan/zoom, y re-parsear 200 features en cada frame de arrastre
  // es exactamente lo que haría sentir pesado al visor.
  const parseadas = useMemo(
    () => marcas.map(m => ({ marca: m, data: parsearMarca(m) })).filter(x => x.data),
    [marcas],
  );

  return (
    <svg
      // F-7: por acá lo agarran la instantánea y la impresión, que CLONAN este
      // SVG en vez de volver a pintar las marcas. Si alguna vez hay dos capas
      // de marcas a la vez, esto deja de ser único y hay que calificarlo.
      data-capa-marcas
      viewBox={`0 0 ${anchoPx} ${altoPx}`}
      width={anchoPx}
      height={altoPx}
      className="absolute left-0 top-0"
      style={{ pointerEvents: 'none' }}
    >
      {parseadas.map(({ marca, data }) => {
        const d = data as MarcaData;
        const sel = idSeleccionada === marca.id;
        return (
          // LAS MARCAS SOLO RECIBEN PUNTEROS CUANDO SE PUEDEN SELECCIONAR.
          // No es cosmética: con una herramienta de dibujo activa, arrastrar
          // por encima de una marca ya existente hacía que Chrome disparara
          // `pointercancel` (arranca a arrastrar el nodo SVG) y el gesto moría
          // en el primer `pointermove`. Resultado medido el 20-oct-2026: un
          // lazo sobre una zona ya anotada se guardaba con DOS vértices, o no
          // se guardaba. Y además es como se comporta Procore: dibujando no se
          // selecciona, se dibuja.
          // El gesto va en el GRUPO y no en la figura: una nota de texto no
          // tiene figura (su dibujo ES la etiqueta), así que colgado de
          // `<Figura>` la nota era la única marca que no se podía agarrar.
          // Acá el evento le llega igual, pintado por donde esté pintado.
          <g key={marca.id}
             style={{
               pointerEvents: onAgarrar ? 'visiblePainted' : 'none',
               cursor: onAgarrar ? 'move' : 'default',
             }}
             onPointerDown={onAgarrar ? ev => onAgarrar(marca.id, ev) : undefined}
             transform={desplazada?.id === marca.id
               ? `translate(${desplazada.dx} ${desplazada.dy})` : undefined}>
            {sel && (
              // Halo: la misma figura con trazo ancho y claro, debajo.
              <g opacity={0.45}>
                <Figura
                  d={{ ...d, properties: { ...d.properties, color: '#ADD010', width: (d.properties?.width || 2) + 6 } }}
                />
              </g>
            )}
            <Figura d={d} />
            <Etiqueta d={d} escala={escala} metrosPorPixel={metrosPorPixel} />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * El rótulo de una marca: la medida recalculada, o la nota si es de texto. El
 * tamaño se divide por la escala para que quede constante en pantalla, igual
 * que el grosor de línea.
 */
function Etiqueta({ d, escala, metrosPorPixel }: {
  d: MarcaData; escala: number; metrosPorPixel?: number | null;
}) {
  const texto = etiqueta(d, metrosPorPixel);
  if (!texto) return null;
  const color = d.properties?.color || '#5D636C';
  const px = (d.properties?.fontSize || 14) / escala;
  const x = etiquetaX(d);
  const y = etiquetaY(d);

  // Cuadro de texto de Procore: papel blanco + borde del color de la marca. El
  // ancho se estima por cantidad de caracteres porque medir texto de verdad
  // exige el DOM, y acá el <text> se pinta dentro de un SVG que escala solo.
  if (d.properties?.conRecuadro) {
    const ancho = texto.length * px * 0.58 + px;
    const alto = px * 1.6;
    return (
      <>
        <rect x={x - px * 0.4} y={y - alto + px * 0.3} width={ancho} height={alto}
          rx={px * 0.25} fill="#fff" stroke={color}
          strokeWidth={2} vectorEffect="non-scaling-stroke" />
        <text x={x} y={y} fontSize={px} fill={color} className="font-sans font-bold">
          {texto}
        </text>
      </>
    );
  }
  return (
    <text x={x} y={y} fontSize={px} fill={color} className="font-sans font-bold"
      style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 4 / escala }}>
      {texto}
    </text>
  );
}

// La etiqueta va en el primer vértice: es donde Fieldwire la pone y evita
// tener que calcular un centroide para cada tipo de geometría.
function primerPunto(d: MarcaData): Punto {
  const c = d.geometry?.coordinates as unknown;
  if (Array.isArray(c) && typeof c[0] === 'number') return c as Punto;
  if (Array.isArray(c) && Array.isArray(c[0])) {
    const primero = c[0] as unknown;
    if (Array.isArray(primero) && typeof primero[0] === 'number') return primero as Punto;
    if (Array.isArray(primero) && Array.isArray(primero[0])) return primero[0] as Punto;
  }
  return [0, 0];
}
const etiquetaX = (d: MarcaData) => primerPunto(d)[0] + 6;
const etiquetaY = (d: MarcaData) => primerPunto(d)[1] - 6;
